import { AlertTriangle, PlayCircle } from "lucide-react";

/**
 * Targeted Soft-Tissue Remedy Card
 *
 * Replaces the standard pre-throwing routine banner whenever the player has
 * flagged any anatomical zone on the Full-Body Visual Scan. If `criticalMode`
 * is true (sharp pain → auto Hybrid D), the card switches to a red emergency
 * style with an explicit "ROM only" callout.
 */
export default function TargetedRemedyCard({ remedies = [], criticalMode = false, sharpPainZones = [] }) {
  if (!remedies || remedies.length === 0) return null;

  const accent = criticalMode ? "#DC2626" : "#F59E0B";
  const bg = criticalMode ? "rgba(220,38,38,0.10)" : "rgba(245,158,11,0.08)";
  const border = criticalMode ? "rgba(220,38,38,0.5)" : "rgba(245,158,11,0.35)";

  return (
    <div
      className="rounded-2xl p-5 border"
      style={{ backgroundColor: bg, borderColor: border }}
      data-testid="targeted-remedy-card"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: accent }} />
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold" style={{ color: accent }}>
            {criticalMode ? "Sharp Pain · Hybrid D Auto-Locked" : "Targeted Remedy Protocol"}
          </div>
          <h2 className="font-display font-black text-2xl text-white mt-1 leading-tight">
            {criticalMode
              ? "Replace today's warmup with these protocols"
              : "Soft-tissue work for the flagged zones"}
          </h2>
          {criticalMode && sharpPainZones.length > 0 && (
            <p className="text-sm text-white/75 mt-2">
              Sharp pain flagged at <b className="text-white">{sharpPainZones.map((z) => z.label).join(", ")}</b>.
              {" "}ROM only today — no throwing. Coaching staff has been notified.
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {remedies.map((r, idx) => (
          <div key={`${r.zone_id}-${idx}`} className="rounded-xl ink-card p-4" data-testid={`remedy-${r.zone_id}`}>
            <div className="flex items-baseline justify-between gap-2">
              <div className="font-display font-bold text-base text-white">{r.protocol}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">{r.zone_label}</div>
            </div>
            <p className="text-xs text-white/60 mt-1">{r.summary}</p>
            {Array.isArray(r.videos) && r.videos.length > 0 && (
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {r.videos.map((v, vi) => (
                  <a
                    key={vi}
                    href={v.url.replace("/embed/", "/watch?v=")}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 rounded-lg px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white"
                    data-testid={`remedy-video-${r.zone_id}-${vi}`}
                  >
                    <PlayCircle className="h-4 w-4 flex-shrink-0" style={{ color: accent }} />
                    <span className="truncate">{v.title}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
